import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ExtDataGrid from "../../common/data-grid/ext-data-grid";
import { APP_VIEW } from "../../constants/constant";
import { getStatusCell } from "../../utils/utils";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalContentText,
  ModalTitle,
} from "@deere/fuel-react";

export const buildManualRequestColumns = ({
  onConfirmRequest,
  openConfirmationModal,
  isConfirming,
  isDeleting,
  canConfirm,
  hasActiveSelection,
}) => {
  const columns = [
    {
      field: "requestStatus",
      headerName: "Request Status",
      width: 150,
      renderCell: (params) => getStatusCell(params.value || "Manual_New"),
    },
    {
      field: "customerNo",
      headerName: "Customer Number",
      width: 150,
    },
    {
      field: "customerName",
      headerName: "Name",
      width: 220,
    },
    {
      field: "assignmentNo",
      headerName: "Assignment Number",
      width: 170,
    },
    {
      field: "amountInDocCurrency",
      headerName: "Amount",
      width: 150,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") return "-";
        const formattedValue = Number(value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return `${formattedValue} ${params.row.currencyKey || ""}`.trim();
      },
    },
    {
      field: "extensionType",
      headerName: "Extension Type",
      width: 150,
    },
    {
      field: "dueDateForNetPayment",
      headerName: "Due Date for Net Payment",
      width: 200,
      renderCell: (params) => {
        if (!params.value) return "-";
        const [year, month, day] = params.value.split("-");
        return `${day}-${month}-${year}`;
      },
    },
    {
      field: "userID",
      headerName: "User ID",
      width: 130,
    },
  ];

  if (canConfirm) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const isManualNew = params.row.requestStatus === "Manual_New";

        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {canConfirm && isManualNew && (
              <Tooltip title={hasActiveSelection ? "Use 'Approve selected requests' above to confirm multiple rows" : ""}>
                <span>
                  <IconButton
                    onClick={() => onConfirmRequest(params.row.id)}
                    color="success"
                    size="small"
                    aria-label="confirm manual request"
                    disabled={isConfirming || hasActiveSelection}
                  >
                    <CheckIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canConfirm && (
              <Tooltip title={hasActiveSelection && isManualNew ? "Use 'Delete selected requests' above to delete multiple rows" : ""}>
                <span>
                  <IconButton
                    onClick={(e) => {
                      e.currentTarget.blur();
                      openConfirmationModal(params.row.id);
                    }}
                    color="error"
                    size="small"
                    disabled={!isManualNew || hasActiveSelection || isDeleting}
                    aria-label="delete manual request"
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      },
    });
  }

  return columns;
};

const ManualRequestTable = ({ data , delete: deleteManualRequests , deleting , selectedRequestIds, onSelectionChange, onConfirmRequest, onConfirmSelected, onDeleteSelected, isConfirming, canConfirm }) => {
  const [isModalOpen , setModelOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const hasActiveSelection = (selectedRequestIds || []).length > 0;
  const columns = buildManualRequestColumns({
    onConfirmRequest,
    openConfirmationModal: (id) => {
      setModelOpen(true);
      setSelectedId(id);
    },
    isConfirming,
    isDeleting: deleting,
    canConfirm,
    hasActiveSelection,
  });

  const handleConfirmDelete = async (id) => {
    await deleteManualRequests([id]);
    setModelOpen(false);
    setSelectedId(null);
  }
  const rows = (data || [])
    .map((request, index) => ({
        ...request,
        id: request.id ?? `${request.accountingDocNo || 'manual-request'}-${request.noOfLineItemWithInAccDoc || index}-${index}`,
    }))
    .sort((firstRow, secondRow) => {
      const statusOrder = { Manual_New: 0, Pending: 1 };
      return (statusOrder[firstRow.requestStatus] ?? 2) - (statusOrder[secondRow.requestStatus] ?? 2);
    });
  const pendingRequestIds = rows
    .filter((row) => row.requestStatus === 'Pending')
    .map((row) => row.id);
  const gridSelectionModel = [...new Set([...pendingRequestIds, ...(selectedRequestIds || [])])];

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "500px",
          backgroundColor: "#ffffff",
          borderRadius: "4px",
          border: "1px solid #eeeeee",
          gap: "16px",
        }}
      >
        <SearchIcon sx={{ fontSize: 48, color: "#367c2b" }} />
        <Typography variant="h6" sx={{ color: "#222222", fontWeight: 600 }}>
          No Manual Requests Created Yet
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#666666", textAlign: "center", maxWidth: "300px" }}
        >
          Click the "New Request" button to create your first manual request
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#ffffff", borderRadius: "4px" }}>
      <ExtDataGrid
        view={APP_VIEW.ERRORS}
        columns={columns}
        rows={rows}
        isLoaderVisible={false}
        disableDetailPanel
        checkboxSelection={canConfirm}
        rowSelectionModel={gridSelectionModel}
        onRowSelectionModelChange={(selectionModel) => {
          onSelectionChange(
            selectionModel.filter((id) => rows.some((row) => row.id === id && row.requestStatus === 'Manual_New')),
          );
        }}
        isRowSelectable={(params) => params.row.requestStatus === 'Manual_New'}
        manualRequestActions={canConfirm ? { onApprove: onConfirmRequest, isConfirming } : undefined}
        confirmSelectedManualRequests={canConfirm ? onConfirmSelected : undefined}
        isConfirmingManualRequests={isConfirming}
        deleteSelectedManualRequests={canConfirm ? onDeleteSelected : undefined}
        isDeletingManualRequests={deleting}
        manualRequestSelectedCount={(selectedRequestIds || []).length}
      />
      
      <Modal open={isModalOpen} onClose={() => setModelOpen(false)}>
        <ModalTitle>Confirm Deletion</ModalTitle>
        <ModalContent>
          <ModalContentText>
            Are you sure you want to delete this manual request?
          </ModalContentText>
        </ModalContent>
        <ModalActions>
          <Button variant="secondary" onClick={() => setModelOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={() => handleConfirmDelete(selectedId)}
            color="error"
            disabled={deleting}
          >
            Delete
          </Button>
        </ModalActions>
      </Modal>
    </Box>
  );
};

export default ManualRequestTable;